/**
 * Menu complet du portail étudiant — extrait de l'Accueil pour désaturer.
 * Organisé en sections claires : Études · Communication · Compte · Aide.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Calendar, MapPin, MessageSquare, Lightbulb, Newspaper, Wifi,
  Settings, History, Bell, AlertCircle, Phone, LogOut, ChevronRight, Cake,
  HelpCircle,
} from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';

interface MenuEntry {
  icon: React.ComponentType<any>;
  label: string;
  sublabel?: string;
  path: string;
  badge?: number;
}

interface Section {
  title: string;
  entries: MenuEntry[];
}

export default function Menu() {
  const navigate = useNavigate();

  const logout = () => {
    if (!window.confirm('Se déconnecter ?')) return;
    portailApi.clearToken();
    navigate('/portail/login', { replace: true });
  };

  const sections: Section[] = [
    {
      title: 'Études',
      entries: [
        { icon: BookOpen,  label: 'Mes cours',            sublabel: 'PDF / Word',        path: '/portail/mes-cours' },
        { icon: BookOpen,  label: 'Banque des sujets',    sublabel: 'Sujets antérieurs', path: '/portail/banque-sujets' },
        { icon: Calendar,  label: 'EDT cours',            sublabel: 'Hebdomadaire',      path: '/portail/emploi-temps-cours' },
        { icon: Calendar,  label: 'EDT examens',          sublabel: 'Sessions',          path: '/portail/emplois-temps' },
        { icon: MapPin,    label: 'Ma salle d\'examen',   sublabel: 'Brassage',          path: '/portail/ma-salle' },
      ],
    },
    {
      title: 'Communication',
      entries: [
        { icon: MessageSquare, label: 'Messages formateurs', path: '/portail/messages-formateur' },
        { icon: MessageSquare, label: 'Messagerie',           sublabel: 'Camarades · Scolarité', path: '/portail/messagerie' },
        { icon: Wifi,          label: 'Étudiants en ligne',   sublabel: 'Voir qui est connecté', path: '/portail/en-ligne' },
        { icon: Newspaper,     label: 'Fil d\'actualité',     sublabel: 'Annonces & événements', path: '/portail/actualites' },
        { icon: Cake,          label: 'Anniversaires',        sublabel: 'Souhaiter à mes camarades', path: '/portail/anniversaires' },
        { icon: HelpCircle,    label: 'Fil d\'entraide',       sublabel: 'Questions, ressources, coups de main', path: '/portail/entraide' },
        { icon: Lightbulb,     label: 'Suggestions',          sublabel: 'Vos idées', path: '/portail/suggestion' },
      ],
    },
    {
      title: 'Mon compte',
      entries: [
        { icon: Bell,        label: 'Notifications',   path: '/portail/notifications' },
        { icon: AlertCircle, label: 'Mes réclamations', path: '/portail/mes-reclamations' },
        { icon: History,     label: 'Mon parcours',    sublabel: 'Historique', path: '/portail/historique' },
        { icon: Settings,    label: 'Réglages',        sublabel: 'Sécurité · Notifications', path: '/portail/reglages' },
      ],
    },
    {
      title: 'Aide',
      entries: [
        { icon: Phone, label: 'Contact scolarité', sublabel: 'Support', path: '/portail/contact' },
      ],
    },
  ];

  return (
    <PortailLayout showHeader title="Menu">
      <div className="space-y-4 pb-6">
        {sections.map((section) => (
          <div key={section.title}>
            <div className="text-[11px] uppercase tracking-widest font-bold text-gray-500 px-2 mb-1.5">
              {section.title}
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden divide-y divide-gray-800/60">
              {section.entries.map((e) => (
                <button
                  key={e.path}
                  onClick={() => navigate(e.path)}
                  className="w-full text-left flex items-center gap-3 px-3 py-3 hover:bg-gray-800/50 transition"
                >
                  <div className="w-9 h-9 rounded-lg bg-gray-800 flex items-center justify-center text-amber-400 flex-shrink-0">
                    <e.icon size={17} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-100 truncate">{e.label}</div>
                    {e.sublabel && (
                      <div className="text-[11px] text-gray-500 truncate">{e.sublabel}</div>
                    )}
                  </div>
                  {!!e.badge && e.badge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-red-500 text-white rounded-full px-1">
                      {e.badge}
                    </span>
                  )}
                  <ChevronRight size={14} className="text-gray-600 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Bouton déconnexion */}
        <button
          onClick={logout}
          className="w-full text-center text-sm text-gray-400 hover:text-red-400 py-3 inline-flex items-center justify-center gap-2 border border-gray-800 rounded-xl bg-gray-900/50 mt-4"
        >
          <LogOut size={14} />
          Se déconnecter
        </button>
      </div>
    </PortailLayout>
  );
}
